import cn from 'classnames';

import useContentHeight from 'js/hooks/use-content-height';
import { CampaignBlock_EditorThemeMap, getEnumStringValue } from 'js/utils/enum-string-maps';

import Image from 'components/image/Image';
import Link from 'components/link/Link';
import Heading from 'components/heading/Heading';

import { ICampaignBlock } from 'source/NFR.types';

interface CampaignBlockProps extends ICampaignBlock {
  listLength?: number;
}

const CampaignBlock: React.FC<CampaignBlockProps> = ({
  cta,
  editorTheme,
  image,
  onPageEditing,
  title,
  description,
  listLength = 1,
  isProminent
}) => {
  const { height, contentRef } = useContentHeight();

  const content = (
    <div
      className={cn('campaign-block', getEnumStringValue(CampaignBlock_EditorThemeMap, editorTheme), {
        'campaign-block--small': listLength > 1,
        'campaign-block--extra-small': listLength > 2,
        'campaign-block--theme-prominent': isProminent
      })}
      ref={contentRef}
    >
      {image && (
        <div className="campaign-block--image">
          <Image
            {...image}
            theme="anchored"
            isFluid={true}
            width={896}
            height={360}
            sources={[
              {
                media: '(min-width: 1600px)',
                width: 896,
                height: 360
              },
              {
                media: '(min-width: 1280px)',
                width: 789,
                height: 360
              },
              {
                media: '(min-width: 1024px)',
                width: 619,
                height: 428
              },
              {
                media: '(min-width: 768px)',
                width: 426,
                height: 454
              },
              {
                media: '(min-width: 510px)',
                width: 462,
                height: 320
              },
              {
                media: '(max-width: 509px)',
                width: 282,
                height: 320
              }
            ]}
            sizes={
              listLength > 2
                ? '(min-width: 1024px) 33vw, (min-width: 768px) 50vw, (min-width: 510px) 462px, 282px'
                : listLength > 1
                  ? '(min-width: 768px) 50vw, (min-width: 510px) 462px, 282px'
                  : '(min-width: 1600px) 896px, (min-width: 1280px) 789px, (min-width: 1024px) 619px, (min-width: 768px) 426px, (min-width: 510px) 462px, 282px'
            }
          />
        </div>
      )}
      <div className="campaign-block--text">
        {title && (
          <Heading className="campaign-block--title" onPageEditing={onPageEditing?.title}>
            {title}
          </Heading>
        )}
        {description && <p className="campaign-block--description">{description}</p>}
        <Link className="campaign-block--link" {...cta} theme="actionLink" />
      </div>
    </div>
  );

  return (
    <>
      {isProminent ? (
        <div className="campaign-block-container" style={{ height }}>
          {content}
        </div>
      ) : (
        content
      )}
    </>
  );
};

CampaignBlock.displayName = 'CampaignBlock';

export default CampaignBlock;
